import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { providersPathTo } from '../utils/Api';
import SnackbarContext from '../contexts/SnackbarContext';
import { SearchContext } from '../contexts/SearchContext';
import { Avatar, Box, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { apiBaseUrl } from '../utils/Api/Api';
import { ShoppingCartProductsContext } from '../contexts/ShoppingCartProductsContext';
import ProductsTagFilters from './ProductsTagFilters';
import { FavoriteBorderRounded, FavoriteRounded, Share } from '@mui/icons-material';
import { ShopProductList } from '.';
import ShoppingCartElement from './building_blocks/ShoppingCartElement';
import { PageBackButton } from './building_blocks/PageHeader';
import OutOfDeliveryAreaPanel from './building_blocks/OutOfDeliveryAreaPanel';
import { layoutShopSheetRadius } from '../theme/colors';
import { useProductTagFilter } from '../hooks/useProductTagFilter';
import SearchResultSectionHeading from './building_blocks/SearchResultSectionHeading';
import { addFavoriteProvider, isFavoriteProvider, removeFavoriteProvider } from '../utils/providerFavoritesStorage';
import { copyPageLink } from '../utils/copyPageLink';
import { formatProviderDeliveryLine } from '../utils/formatDistance';
import { getOutsideDeliveryAreaError, originParamsFromUser } from '../utils/providerListParams';
import { UserContext } from '../contexts/UserContext';
import { productOnSale, shopCatalogProducts } from './building_blocks/Currency';

function ProviderShop() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [provider, setProvider] = useState();
  const [loadState, setLoadState] = useState('loading');
  const [blockedRadiusKm, setBlockedRadiusKm] = useState(30);
  const [isFavorite, setIsFavorite] = useState(false);
  const promoFilter = searchParams.get('promo') === '1';
  const allProducts = provider?.products;
  const catalog = useMemo(
    () => shopCatalogProducts(allProducts, promoFilter),
    [allProducts, promoFilter]
  );
  const hasOnSaleProducts = useMemo(
    () => (allProducts || []).some(productOnSale),
    [allProducts]
  );
  const { tagFilter, setTagFilter, catalogTags, filteredProducts, resultSections } = useProductTagFilter(catalog);
  const { searchTags, fromSearch, setFromSearch } = useContext(SearchContext);
  const { shoppingCartProducts, setShoppingCartProducts } = useContext(ShoppingCartProductsContext);
  const navigate = useNavigate();
  const { openSnackbar } = useContext(SnackbarContext);
  const { currentUser } = useContext(UserContext);

  const handlePromoFilterChange = (nextEnabled) => {
    const nextParams = new URLSearchParams(searchParams);
    if (nextEnabled) {
      nextParams.set('promo', '1');
    } else {
      nextParams.delete('promo');
    }
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    if (searchTags.length > 0 && fromSearch) {
      setTagFilter(searchTags);
      setFromSearch(false);
    }
  }, [searchTags, fromSearch, setTagFilter, setFromSearch]);

  const handleAddProduct = (product, index) => {
    if (shoppingCartProducts.length > 0 && shoppingCartProducts[0].provider_id !== product.provider_id) {
      openSnackbar('Você não pode adicionar produtos de lojas diferentes ao carrinho', 'info');
      return;
    }
    const newCartProduct = {
      ...product,
      cartQuantity: 1,
      productIndex: index
    };
    setShoppingCartProducts([...shoppingCartProducts, newCartProduct]);
    localStorage.setItem('shoppingCartProducts', JSON.stringify([...shoppingCartProducts, newCartProduct]));
    localStorage.setItem('shoppingProvider', JSON.stringify(provider));
  };

  const changeCartQuantity = (id, operation) => {
    let newShoppingCartProducts = [...shoppingCartProducts];
    const cartProduct = shoppingCartProducts.find(product => product.id === id);
    if (operation === '+') {
      if (cartProduct.quantity === 0 || (cartProduct.cartQuantity < cartProduct.quantity))
        cartProduct.cartQuantity += 1;
    } else {
      cartProduct.cartQuantity -= 1;
    }
    if (cartProduct.cartQuantity < 1) {
      var index = newShoppingCartProducts.findIndex(item => item.id === id);
      newShoppingCartProducts.splice(index, 1);
    }
    setShoppingCartProducts(newShoppingCartProducts);
    localStorage.setItem('shoppingCartProducts', JSON.stringify(newShoppingCartProducts));
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    setLoadState('loading');
    const params = originParamsFromUser(currentUser);

    providersPathTo.provider_with_products({ id, params })
      .then((result) => {
        setProvider(result.data);
        setLoadState('ok');
      })
      .catch((err) => {
        const blocked = getOutsideDeliveryAreaError(err);
        if (blocked) {
          setBlockedRadiusKm(blocked.radiusKm);
          setLoadState('blocked');
          return;
        }

        openSnackbar('Não foi possível carregar a loja', 'error');
        setLoadState('error');
      });
  }, [id, currentUser, openSnackbar]);

  useEffect(() => {
    if (!id) {
      return;
    }

    setIsFavorite(isFavoriteProvider(id));
  }, [id]);

  useEffect(() => {
    if (!provider) {
      return;
    }
    document.title = `${provider.name} - TudoPets.app`;
  }, [provider]);

  if (loadState === 'loading') {
    return <div>Carregando...</div>;
  }

  if (loadState === 'blocked') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PageBackButton onClick={() => navigate('/home')} />
        <OutOfDeliveryAreaPanel
          radiusKm={blockedRadiusKm}
          compact
          returnTo={`/provider/${id}`}
        />
      </Box>
    );
  }

  if (loadState === 'error' || !provider) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <PageBackButton onClick={() => window.history.back()} />
        <Typography sx={{ fontFamily: 'Nunito', color: 'text.secondary' }}>
          Não foi possível carregar esta loja.
        </Typography>
      </Box>
    );
  }

  const handleFavoriteToggle = () => {
    if (isFavorite) {
      removeFavoriteProvider(provider.id);
    } else {
      addFavoriteProvider(provider);
    }

    setIsFavorite(!isFavorite);
    openSnackbar(isFavorite ? 'Loja removida dos favoritos' : 'Loja adicionada aos favoritos', isFavorite ? 'info' : 'success');
  };

  const handleShareClick = async () => {
    try {
      await copyPageLink();
      openSnackbar('Link copiado para a área de transferência', 'success');
    } catch {
      openSnackbar('Não foi possível copiar o link', 'error');
    }
  };

  const favoriteLabel = isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos';
  const actionButtonSx = {
    width: 40,
    height: 40,
    bgcolor: alpha('#1B1B1B', 0.58),
    color: '#FFFFFF',
    boxShadow: 'none',
    '&:hover': {
      bgcolor: alpha('#1B1B1B', 0.72),
    },
    '& .MuiSvgIcon-root': {
      fontSize: 24,
    },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 32px)', gap: 1 }}>
      <Box id="shop-top" sx={{ alignItems: 'center', display: 'flex', justifyContent: 'flex-start' }}>
        <PageBackButton onClick={() => window.history.back()} />
      </Box>
      <Box id="shop-main" sx={{ position: 'relative', display: 'flex', alignItems: 'center', borderRadius: `${layoutShopSheetRadius} ${layoutShopSheetRadius} 0 0`, bgcolor: 'surface.card', flex: 1, flexDirection: 'column', alignSelf: 'stretch', justifyContent: 'flex-start', width: { xs: 'calc(100% + 32px)', sm: '100%' }, mx: { xs: -2, sm: 0 }, pb: resultSections.length === 0 && shoppingCartProducts.length > 0 ? '118px' : undefined }}>
        <Box sx={{ position: 'absolute', top: 16, right: 16, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Compartilhar">
            <IconButton
              aria-label="Compartilhar"
              onClick={handleShareClick}
              sx={actionButtonSx}
            >
              <Share sx={{ transform: 'translateX(-1px)' }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={favoriteLabel}>
            <IconButton
              aria-label={favoriteLabel}
              onClick={handleFavoriteToggle}
              sx={(theme) => ({
                ...actionButtonSx,
                '& .MuiSvgIcon-root': {
                  fontSize: 24,
                  filter: isFavorite ? `drop-shadow(0 0 1px ${theme.palette.primary.main})` : 'none',
                },
              })}
            >
              {isFavorite ? <FavoriteRounded sx={{ color: 'primary.main' }} /> : <FavoriteBorderRounded />}
            </IconButton>
          </Tooltip>
        </Box>
        <Avatar sx={{ border: '1px solid lightgray', width: '80px', height: '80px', marginTop: '-32px', bgcolor: '#CFD8DC' }} src={provider.business_logo && `${apiBaseUrl}${provider.business_logo}`} />
        <Box sx={{
          mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'
        }}>
          <Typography variant="pageTitle"><b>{provider.name}</b></Typography>
          <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', fontWeight: 400, lineHeight: '24px', color: '#546E7A' }}>
            <small>{formatProviderDeliveryLine(provider.distance_km)}</small>
          </Typography>
          <Typography color="primary" onClick={() => navigate('/shop_info/', { state: provider })}
            sx={{
              fontFamily: 'Nunito', fontSize: '16px', fontWeight: 400, lineHeight: '14px', textDecorationLine: 'underline',
              textDecorationStyle: 'solid',
              textDecorationSkipInk: 'auto',
              textDecorationThickness: 'auto',
              textUnderlineOffset: '3px',
              textUnderlinePosition: 'from-font',
              cursor: 'pointer'
            }}>Ver perfil</Typography>
        </Box>

        <ProductsTagFilters
          tagFilter={tagFilter}
          setTagFilter={setTagFilter}
          tags={catalogTags}
          numProducts={filteredProducts.length}
          defaultExpanded={false}
          sx={{ px: 2, width: '100%' }}
          promoFilter={promoFilter}
          onPromoFilterChange={handlePromoFilterChange}
          showPromoFilter={hasOnSaleProducts || promoFilter}
        />

        {promoFilter && catalog.length === 0 && (
          <Typography
            sx={{
              px: 2,
              pt: 2,
              fontFamily: 'Nunito',
              fontSize: '14px',
              lineHeight: '21px',
              color: 'text.secondary',
              alignSelf: 'stretch',
            }}
          >
            Nenhum produto em promoção no momento.
          </Typography>
        )}

        {catalog.length > 0 && resultSections.map((section, sectionIndex) => {
          const isLast = sectionIndex === resultSections.length - 1;
          const showHeading = section.matchedTags.length > 0;

          return (
            <Box
              key={section.key}
              sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignSelf: 'stretch', width: '100%' }}
            >
              {showHeading && (
                <Box sx={{ px: 2 }}>
                  <SearchResultSectionHeading matchedTags={section.matchedTags} />
                </Box>
              )}
              <ShopProductList
                products={section.products}
                catalogProducts={provider?.products}
                shoppingCartProducts={shoppingCartProducts}
                handleAddProduct={handleAddProduct}
                changeCartQuantity={changeCartQuantity}
                sx={{
                  mt: showHeading ? 0 : 3,
                  pt: showHeading ? 0 : undefined,
                  pb: isLast && shoppingCartProducts.length > 0 ? '118px' : 2,
                }}
              />
            </Box>
          );
        })}
      </Box>
      <ShoppingCartElement />
    </Box>
  );
}

export default ProviderShop;
